import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

export type VlmSettingsKey =
  | "environmentalHazards"
  | "lighting"
  | "entryway"
  | "lossPrevention"
  | "leftObjectDetection"
  | "customAnalysis";

export type VlmSettings = Record<VlmSettingsKey, boolean>;

export const DEFAULT_VLM_SETTINGS: VlmSettings = {
  environmentalHazards: false,
  lighting: false,
  entryway: false,
  lossPrevention: false,
  leftObjectDetection: false,
  customAnalysis: false,
};

export const DEFAULT_CUSTOM_ANALYSIS_NAME = "";

const columnSx = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  flex: 1,
  minWidth: 140,
};

const labelSx = {
  fontFamily: "Mulish",
  fontWeight: 700,
  fontSize: 14,
  textAlign: "center",
  mb: 1,
};

const subLabelSx = {
  fontFamily: "Mulish",
  fontWeight: 600,
  fontSize: 12,
  textAlign: "center",
  mb: 0.5,
};

const customAnalysisNameSx = {
  fontFamily: "Mulish",
  fontSize: 13,
  textAlign: "center",
  mt: 1,
  width: "100%",
  minHeight: 24,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

type VlmTabTogglesProps = {
  settings: VlmSettings;
  customAnalysisName: string;
  disabled: boolean;
  onChange?: (key: VlmSettingsKey, checked: boolean) => void;
  onCustomAnalysisNameChange?: (value: string) => void;
};

const ToggleColumn = ({
  label,
  settingKey,
  settings,
  disabled,
  onChange,
}: {
  label: string;
  settingKey: VlmSettingsKey;
  settings: VlmSettings;
  disabled: boolean;
  onChange?: (key: VlmSettingsKey, checked: boolean) => void;
}) => (
  <Box sx={columnSx}>
    <Typography sx={labelSx}>{label}</Typography>
    <Switch
      checked={settings[settingKey]}
      color="warning"
      disabled={disabled}
      onChange={(event) => onChange?.(settingKey, event.target.checked)}
      inputProps={{ "aria-label": label }}
    />
  </Box>
);

export const VlmTabToggles = ({
  settings,
  customAnalysisName,
  disabled,
  onChange,
  onCustomAnalysisNameChange,
}: VlmTabTogglesProps) => (
  <Paper>
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        gap: 2,
        p: 3,
        fontFamily: "Mulish",
        flexWrap: "wrap",
        justifyContent: "space-between",
      }}
    >
      <ToggleColumn
        label="Environmental Hazards"
        settingKey="environmentalHazards"
        settings={settings}
        disabled={disabled}
        onChange={onChange}
      />
      <ToggleColumn
        label="Lighting"
        settingKey="lighting"
        settings={settings}
        disabled={disabled}
        onChange={onChange}
      />
      <ToggleColumn
        label="Entryway"
        settingKey="entryway"
        settings={settings}
        disabled={disabled}
        onChange={onChange}
      />
      <Box sx={{ ...columnSx, minWidth: 220 }}>
        <Typography sx={labelSx}>Compare with Last Patrol</Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 2,
            mt: 1,
            justifyContent: "center",
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography sx={subLabelSx}>Loss Prevention</Typography>
            <Switch
              checked={settings.lossPrevention}
              color="warning"
              disabled={disabled}
              onChange={(event) =>
                onChange?.("lossPrevention", event.target.checked)
              }
              inputProps={{ "aria-label": "Loss Prevention" }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography sx={subLabelSx}>Left Object Detection</Typography>
            <Switch
              checked={settings.leftObjectDetection}
              color="warning"
              disabled={disabled}
              onChange={(event) =>
                onChange?.("leftObjectDetection", event.target.checked)
              }
              inputProps={{ "aria-label": "Left Object Detection" }}
            />
          </Box>
        </Box>
      </Box>
      <Box sx={{ ...columnSx, minWidth: 180 }}>
        <Typography sx={labelSx}>Custom Analysis</Typography>
        <Switch
          checked={settings.customAnalysis}
          color="warning"
          disabled={disabled}
          onChange={(event) =>
            onChange?.("customAnalysis", event.target.checked)
          }
          inputProps={{ "aria-label": "Custom Analysis" }}
        />
        {disabled ? (
          <Typography sx={customAnalysisNameSx}>
            {customAnalysisName}
          </Typography>
        ) : (
          <TextField
            value={customAnalysisName}
            onChange={(event) =>
              onCustomAnalysisNameChange?.(event.target.value)
            }
            variant="outlined"
            multiline
            rows={3}
            placeholder="Enter custom analysis name"
            sx={{
              mt: 1,
              width: "100%",
              "& .MuiInputBase-root": { fontFamily: "Mulish" },
              "& .MuiInputBase-input": { fontFamily: "Mulish" },
            }}
            inputProps={{
              "aria-label": "Custom analysis name",
            }}
          />
        )}
      </Box>
    </Box>
  </Paper>
);
